import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronRight, Download, Loader2, Plus, Search, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { rp } from "@/lib/payroll";
import { useT } from "@/i18n";
import { TID } from "@/constants/testIds";
import NumField from "@/components/NumField";
import ConfirmDialog from "@/components/ConfirmDialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

export default function Dashboard() {
  const { t, months } = useT();
  const { readOnly } = useAuth();
  const [periods, setPeriods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [bulan, setBulan] = useState(String(new Date().getMonth() + 1));
  const [tahun, setTahun] = useState(new Date().getFullYear());
  const [busy, setBusy] = useState(false);
  const [delTarget, setDelTarget] = useState(null);
  const [query, setQuery] = useState("");
  const [exportOpen, setExportOpen] = useState(false);
  const [startP, setStartP] = useState("");
  const [endP, setEndP] = useState("");
  const [exporting, setExporting] = useState(false);
  const navigate = useNavigate();

  const pval = (p) => `${p.tahun}-${String(p.bulan).padStart(2, "0")}`;
  const periodsAsc = [...periods].sort(
    (a, b) => a.tahun - b.tahun || a.bulan - b.bulan
  );

  const openExport = () => {
    if (periodsAsc.length === 0) return;
    setStartP(pval(periodsAsc[0]));
    setEndP(pval(periodsAsc[periodsAsc.length - 1]));
    setExportOpen(true);
  };

  const exportRange = async () => {
    setExporting(true);
    try {
      const res = await api.get("/xlsx/rekap-range", {
        params: { start: startP, end: endP },
        responseType: "blob",
      });
      const disp = res.headers["content-disposition"] || "";
      const match = disp.match(/filename="?([^"]+)"?/);
      const link = document.createElement("a");
      link.href = URL.createObjectURL(res.data);
      link.download = match ? match[1] : "Rekap-Gaji.xlsx";
      document.body.appendChild(link);
      link.click();
      link.remove();
      setExportOpen(false);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setExporting(false);
    }
  };

  const load = async () => {
    try {
      const { data } = await api.get("/periods");
      setPeriods(data);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const create = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/periods", {
        bulan: Number(bulan),
        tahun: Number(tahun),
      });
      setOpen(false);
      navigate(`/periode/${data.id}`);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!delTarget) return;
    try {
      await api.delete(`/periods/${delTarget.id}`);
      setPeriods((p) => p.filter((x) => x.id !== delTarget.id));
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setDelTarget(null);
    }
  };

  return (
    <div data-testid={TID.dashboard.root} className="space-y-7">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-[26px] font-semibold tracking-tight sm:text-[30px]">
            {t("nav.payroll")}
          </h1>
        </div>
        <div className="flex items-center gap-2">
          <button
            data-testid={TID.dashboard.exportExcel}
            onClick={openExport}
            disabled={periods.length === 0}
            className="flex h-10 items-center gap-1.5 rounded-full border border-[#E1E1E6] bg-white px-4 text-[14px] font-medium text-[#1D1D1F] transition-all hover:bg-[#F2F2F7] active:scale-[0.98] disabled:opacity-40"
          >
            <Download className="h-4 w-4" /> {t("pay.excel")}
          </button>
          <button
            data-testid={TID.dashboard.newPeriod}
            onClick={() => setOpen(true)}
            className={`flex h-10 items-center gap-1.5 rounded-full bg-[#0071E3] px-4 text-[14px] font-medium text-white transition-all hover:bg-[#0077ED] active:scale-[0.98] ${readOnly ? "hidden" : ""}`}
          >
            <Plus className="h-4 w-4" /> {t("pay.newPeriod")}
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 py-24 text-[14px] text-[#8E8E93]">
          <Loader2 className="h-4 w-4 animate-spin" />
        </div>
      ) : periods.length === 0 ? (
        <div className="card-soft px-6 py-20 text-center text-[14px] text-[#8E8E93]">
          {t("pay.noPeriods")}
        </div>
      ) : (
        <>
          <div className="relative max-w-sm">
            <Search className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-[#B0B0B8]" />
            <input
              data-testid={TID.dashboard.search}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t("pay.searchMonth")}
              className="focus-ring h-11 w-full rounded-full border border-[#E1E1E6] bg-white pl-10 pr-4 text-[15px] placeholder:text-[#B0B0B8]"
            />
          </div>
          {(() => {
            const q = query.trim().toLowerCase();
            const shown = q
              ? periods.filter((p) =>
                  `${months[p.bulan - 1]} ${p.tahun}`.toLowerCase().includes(q))
              : periods;
            if (shown.length === 0)
              return (
                <div className="card-soft px-6 py-16 text-center text-[14px] text-[#8E8E93]">
                  {t("pay.noMatch", { q: query })}
                </div>
              );
            return (
              <div
                data-testid={TID.dashboard.list}
                className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3"
              >
                {shown.map((p, i) => (
            <div
              key={p.id}
              data-testid={TID.dashboard.card(p.id)}
              className="card-soft lift fade-up group p-5"
              style={{ animationDelay: `${i * 40}ms` }}
            >
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="text-[17px] font-semibold tracking-tight">
                    {months[p.bulan - 1]} {p.tahun}
                  </h3>
                  <p className="num mt-2 text-[22px] font-semibold tracking-tight">
                    Rp {rp(p.summary.total_keseluruhan)}
                  </p>
                </div>
                <button
                  data-testid={TID.dashboard.del(p.id)}
                  onClick={() => setDelTarget(p)}
                  className={`grid h-8 w-8 place-items-center rounded-full text-[#B0B0B8] transition-colors hover:bg-[#FDF0F2] hover:text-[#C8102E] ${readOnly ? "hidden" : ""}`}
                  aria-label="Delete period"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>

              <div className="mt-4 space-y-1.5 border-t border-black/[0.06] pt-4 text-[13px]">
                <div className="flex justify-between">
                  <span className="text-[#8E8E93]">{t("pay.transfer")} ({p.summary.count_transfer})</span>
                  <span className="num font-medium">{rp(p.summary.subtotal_transfer)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[#8E8E93]">{t("pay.cash")} ({p.summary.count_tunai})</span>
                  <span className="num font-medium">{rp(p.summary.subtotal_tunai)}</span>
                </div>
              </div>

              <button
                data-testid={TID.dashboard.open(p.id)}
                onClick={() => navigate(`/periode/${p.id}`)}
                className="mt-4 flex h-10 w-full items-center justify-center gap-1 rounded-xl bg-[#F2F2F7] text-[14px] font-medium text-[#1D1D1F] transition-colors hover:bg-[#E7E7EC]"
              >
                {t("pay.open")}
                <ChevronRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
              </button>
            </div>
          ))}
              </div>
            );
          })()}
        </>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          data-testid={TID.dashboard.createDialog}
          className="gap-0 rounded-[20px] border border-white/50 bg-white/70 p-6 shadow-[0_20px_60px_rgba(0,0,0,0.18)] ring-1 ring-inset ring-white/50 backdrop-blur-2xl backdrop-saturate-150 sm:max-w-[400px]"
        >
          <DialogHeader className="text-left">
            <DialogTitle className="text-[19px] font-semibold tracking-tight">
              {t("pay.newPeriodTitle")}
            </DialogTitle>
          </DialogHeader>
          <div className="mt-5 grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <label className="label block">{t("pay.monthLbl")}</label>
              <Select value={bulan} onValueChange={setBulan}>
                <SelectTrigger
                  data-testid={TID.dashboard.bulan}
                  className="h-11 rounded-xl border-[#E1E1E6] bg-[#FAFAFA] text-[15px]"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="rounded-xl">
                  {months.map((b, i) => (
                    <SelectItem key={b} value={String(i + 1)}>
                      {b}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <label className="label block">{t("pay.yearLbl")}</label>
              <NumField
                testid={TID.dashboard.tahun}
                value={tahun}
                onChange={setTahun}
                align="text-left"
                className="h-11 w-full text-[15px]"
              />
            </div>
          </div>
          <button
            data-testid={TID.dashboard.confirm}
            onClick={create}
            disabled={busy}
            className="mt-6 flex h-11 w-full items-center justify-center gap-2 rounded-xl bg-[#0071E3] text-[15px] font-medium text-white transition-all hover:bg-[#0077ED] active:scale-[0.99] disabled:opacity-50"
          >
            {busy && <Loader2 className="h-4 w-4 animate-spin" />}
            {t("pay.create")}
          </button>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={!!delTarget}
        onOpenChange={(v) => !v && setDelTarget(null)}
        title={t("pay.delPeriodTitle")}
        description={
          delTarget
            ? t("pay.delPeriodDesc", { label: `${months[delTarget.bulan - 1]} ${delTarget.tahun}` })
            : ""
        }
        onConfirm={remove}
        testid={TID.dashboard.delDialog}
        confirmTestid={TID.dashboard.delConfirm}
      />

      <Dialog open={exportOpen} onOpenChange={setExportOpen}>
        <DialogContent
          data-testid={TID.dashboard.exportDialog}
          className="gap-0 rounded-[20px] border border-white/50 bg-white/70 p-6 shadow-[0_20px_60px_rgba(0,0,0,0.18)] ring-1 ring-inset ring-white/50 backdrop-blur-2xl backdrop-saturate-150 sm:max-w-[420px]"
        >
          <DialogHeader className="text-left">
            <DialogTitle className="text-[19px] font-semibold tracking-tight">
              {t("pay.exportExcel")}
            </DialogTitle>
          </DialogHeader>
          <p className="mt-1 text-[13.5px] text-[#8E8E93]">
            {t("pay.exportDesc")}
          </p>
          <div className="mt-5 grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <label className="label block">{t("pay.from")}</label>
              <Select value={startP} onValueChange={setStartP}>
                <SelectTrigger
                  data-testid={TID.dashboard.exportStart}
                  className="h-11 rounded-xl border-[#E1E1E6] bg-[#FAFAFA] text-[15px]"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="rounded-xl">
                  {periodsAsc.map((p) => (
                    <SelectItem key={pval(p)} value={pval(p)}>
                      {months[p.bulan - 1]} {p.tahun}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <label className="label block">{t("pay.to")}</label>
              <Select value={endP} onValueChange={setEndP}>
                <SelectTrigger
                  data-testid={TID.dashboard.exportEnd}
                  className="h-11 rounded-xl border-[#E1E1E6] bg-[#FAFAFA] text-[15px]"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="rounded-xl">
                  {periodsAsc.map((p) => (
                    <SelectItem key={pval(p)} value={pval(p)}>
                      {months[p.bulan - 1]} {p.tahun}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <button
            data-testid={TID.dashboard.exportConfirm}
            onClick={exportRange}
            disabled={exporting || !startP || !endP}
            className="mt-6 flex h-11 w-full items-center justify-center gap-2 rounded-xl bg-[#0071E3] text-[15px] font-medium text-white transition-all hover:bg-[#0077ED] active:scale-[0.99] disabled:opacity-50"
          >
            {exporting ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Download className="h-4 w-4" />
            )}
            {t("pay.downloadExcel")}
          </button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
